import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Twitter, Linkedin, Instagram, Send, Sparkles, MessageSquare, Inbox, FileEdit,
  Loader2, RefreshCw, Plus, Hash, Megaphone, TrendingUp, Pause, Play, Trash2, Link as LinkIcon, X, CheckCircle2,
  Settings, Facebook, Eye, EyeOff, ChevronDown, ChevronUp, ExternalLink, Heart, MessageCircle, Image as ImageIcon
} from "lucide-react";
import { socialApi, mailDraftApi, campaignsApi, socialAuthApi, metaApi } from "../api";

const PLATFORMS = [
  { id: "twitter", label: "Twitter / X", Icon: Twitter, color: "#8b5cf6" },
  { id: "linkedin", label: "LinkedIn", Icon: Linkedin, color: "#a78bfa" },
  { id: "instagram", label: "Instagram", Icon: Instagram, color: "#f472b6" },
];

const platMeta = (id) => PLATFORMS.find(p => p.id === id) || PLATFORMS[0];

function PlatformChip({ id }) {
  const p = platMeta(id);
  const { Icon } = p;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 mono text-[9px] uppercase tracking-widest"
      style={{ background: "rgba(139,92,246,0.08)", border: `1px solid ${p.color}33`, color: p.color }}
    >
      <Icon size={10} /> {p.label}
    </span>
  );
}

function TabBtn({ active, onClick, children, testid, badge }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-2 mono text-[11px] uppercase tracking-widest flex items-center gap-2 transition-colors ${
        active ? "text-[#3b82f6]" : "text-[#c7c1ff]/60 hover:text-[#8b5cf6]"
      }`}
      style={{
        borderBottom: active ? "2px solid #3b82f6" : "2px solid transparent",
      }}
    >
      {children}
      {badge ? (
        <span className="ml-1 px-1.5 py-0.5 text-[9px] bg-[#3b82f6] text-[#0b1014] rounded-sm">{badge}</span>
      ) : null}
    </button>
  );
}

function ComposerCard({ onDrafted }) {
  const [platform, setPlatform] = useState("twitter");
  const [topic, setTopic] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const draft = async () => {
    if (!topic.trim()) { setErr("Give the AI a topic to write about."); return; }
    setBusy(true); setErr("");
    try {
      const d = await socialApi.draftPost(platform, topic);
      setBody(""); setTopic("");
      onDrafted?.(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to draft");
    }
    setBusy(false);
  };

  const publishManual = async () => {
    if (!body.trim()) { setErr("Write something to publish, or use AI Draft."); return; }
    setBusy(true); setErr("");
    try {
      await socialApi.createPostManual(platform, body);
      setBody("");
      onDrafted?.(null);
    } catch (e) {
      setErr("Failed to publish");
    }
    setBusy(false);
  };

  return (
    <div className="panel p-4 space-y-3" data-testid="social-composer">
      <div className="flex items-center gap-2">
        <div className="mono text-[10px] uppercase tracking-widest text-[#c7c1ff]/60">// new post</div>
        <div className="ml-auto flex items-center gap-1">
          {PLATFORMS.map(p => {
            const { Icon } = p;
            const active = platform === p.id;
            return (
              <button
                key={p.id}
                data-testid={`composer-platform-${p.id}`}
                onClick={() => setPlatform(p.id)}
                className={`p-1.5 transition-colors ${active ? "text-[#3b82f6]" : "text-[#c7c1ff]/40 hover:text-[#8b5cf6]"}`}
                title={p.label}
                style={{ borderBottom: active ? "1px solid #3b82f6" : "1px solid transparent" }}
              >
                <Icon size={14} />
              </button>
            );
          })}
        </div>
      </div>

      <input
        data-testid="composer-topic"
        value={topic}
        onChange={(e) => setTopic(e.target.value)}
        placeholder="What should the AI write about? e.g. 'shipped a new feature, thank early users'"
        className="w-full bg-[#0e1a22] border border-[#8b5cf6]/20 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60 mono"
      />

      <textarea
        data-testid="composer-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="…or write a post manually and publish directly"
        rows={3}
        className="w-full bg-[#0e1a22] border border-[#8b5cf6]/20 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60"
      />

      {err && <div className="text-xs text-[#f87171] mono">{err}</div>}

      <div className="flex items-center gap-2">
        <button
          data-testid="composer-ai-draft"
          onClick={draft}
          disabled={busy}
          className="btn-accent flex items-center gap-2 text-[11px] uppercase tracking-widest mono"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
          AI Draft → Approval
        </button>
        <button
          data-testid="composer-publish"
          onClick={publishManual}
          disabled={busy || !body.trim()}
          className="btn-ghost flex items-center gap-2 text-[11px] uppercase tracking-widest mono"
        >
          <Send size={13} /> publish direct
        </button>
        <div className="ml-auto mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/40">
          ai output is queued for human approval
        </div>
      </div>
    </div>
  );
}

function FeedTab({ onDrafted }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setPosts(await socialApi.posts()); } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex flex-col gap-4 p-4 overflow-y-auto h-full">
      <ComposerCard onDrafted={(d) => { onDrafted?.(d); load(); }} />

      <div className="flex items-center justify-between">
        <div className="mono text-[10px] uppercase tracking-widest text-[#c7c1ff]/60">// published feed · {posts.length}</div>
        <button onClick={load} className="text-[#c7c1ff]/60 hover:text-[#8b5cf6]" title="Refresh"><RefreshCw size={13} /></button>
      </div>

      {loading ? <div className="text-xs mono text-[#c7c1ff]/50">loading…</div> :
       posts.length === 0 ? <div className="text-xs mono text-[#c7c1ff]/50">no posts yet · draft one above</div> :
       posts.map(p => (
         <div key={p.id} data-testid={`social-post-${p.id}`} className="panel p-3 space-y-2">
           <div className="flex items-center gap-2">
             <PlatformChip id={p.platform} />
             {p.source === "ai-approved" && (
               <span className="mono text-[9px] uppercase tracking-widest text-[#3b82f6] flex items-center gap-1">
                 <Sparkles size={9} /> ai-assisted · approved
               </span>
             )}
             <span className="ml-auto mono text-[9px] text-[#c7c1ff]/40">{new Date(p.ts).toLocaleString()}</span>
           </div>
           <div className="text-sm whitespace-pre-wrap text-[#e8f4f8]">{p.body}</div>
           <div className="flex gap-4 mono text-[10px] text-[#c7c1ff]/40 pt-1 border-t border-[#8b5cf6]/10">
             <span>♥ {p.stats?.likes ?? 0}</span>
             <span>💬 {p.stats?.comments ?? 0}</span>
             <span>↗ {p.stats?.shares ?? 0}</span>
           </div>
         </div>
       ))
      }
    </div>
  );
}

function InboxTab({ onDrafted }) {
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadThreads = useCallback(async () => {
    try { setThreads(await socialApi.dms()); } catch { /* ignore */ }
  }, []);

  const loadThread = useCallback(async (id) => {
    setSelected(id);
    try { setThread(await socialApi.dmThread(id)); } catch { /* ignore */ }
    loadThreads();
  }, [loadThreads]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  const aiReply = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const d = await socialApi.draftDmReply(selected);
      onDrafted?.(d);
    } catch { /* ignore */ }
    setBusy(false);
  };

  const simulate = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await socialApi.simulateInbound(selected, null);
      await loadThread(selected);
    } catch { /* ignore */ }
    setBusy(false);
  };

  return (
    <div className="flex h-full">
      <div className="w-72 border-r border-[#8b5cf6]/15 overflow-y-auto">
        <div className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-[#c7c1ff]/60 border-b border-[#8b5cf6]/15">// dm threads · {threads.length}</div>
        {threads.map(t => {
          const meta = platMeta(t.platform);
          const { Icon } = meta;
          return (
            <button
              key={t.id}
              data-testid={`dm-thread-${t.id}`}
              onClick={() => loadThread(t.id)}
              className={`w-full text-left px-3 py-3 border-b border-[#8b5cf6]/10 hover:bg-[#8b5cf6]/5 flex items-center gap-3 ${selected === t.id ? "bg-[#8b5cf6]/10" : ""}`}
            >
              <span
                className="w-9 h-9 flex items-center justify-center font-bold text-xs mono"
                style={{
                  background: t.contact_avatar + "22",
                  border: `1px solid ${t.contact_avatar}55`,
                  color: t.contact_avatar,
                  clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                }}
              >{t.contact_name.split(" ").map(p => p[0]).join("").slice(0,2)}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <Icon size={10} color={meta.color} />
                  <span className="text-sm truncate">{t.contact_name}</span>
                  {t.unread && <span className="w-1.5 h-1.5 bg-[#3b82f6] rounded-full" />}
                </div>
                <div className="mono text-[10px] text-[#c7c1ff]/40 truncate">{t.contact_handle}</div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex-1 flex flex-col">
        {!thread ? (
          <div className="flex-1 flex items-center justify-center mono text-xs text-[#c7c1ff]/40">
            select a thread to view conversation
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-[#8b5cf6]/15 flex items-center gap-3">
              <span
                className="w-8 h-8 flex items-center justify-center text-xs font-bold mono"
                style={{
                  background: thread.thread.contact_avatar + "22",
                  color: thread.thread.contact_avatar,
                  clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                }}
              >{thread.thread.contact_name.split(" ").map(p => p[0]).join("").slice(0,2)}</span>
              <div>
                <div className="text-sm font-medium">{thread.thread.contact_name}</div>
                <div className="mono text-[10px] text-[#c7c1ff]/40 flex items-center gap-2">
                  <PlatformChip id={thread.thread.platform} />
                  {thread.thread.contact_handle}
                </div>
              </div>
              <div className="ml-auto flex gap-2">
                <button data-testid="dm-simulate" onClick={simulate} disabled={busy} className="btn-ghost text-[10px] uppercase tracking-widest flex items-center gap-1">
                  <Plus size={11} /> sim inbound
                </button>
                <button data-testid="dm-ai-reply" onClick={aiReply} disabled={busy} className="btn-accent text-[10px] uppercase tracking-widest flex items-center gap-1">
                  {busy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                  AI Draft Reply
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {thread.messages.map(m => (
                <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[68%] px-3 py-2 text-sm ${m.direction === "out" ? "bg-[#8b5cf6]/15 text-[#e8f4f8]" : "bg-[#1d3240] text-[#e8f4f8]"}`}
                    style={{ borderRadius: 2, border: "1px solid rgba(139,92,246,0.15)" }}
                  >
                    {m.body}
                    <div className="mono text-[9px] text-[#c7c1ff]/40 mt-1 flex items-center gap-1">
                      {m.ai_assisted && <Sparkles size={8} />}
                      {new Date(m.ts).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============ META LIVE TAB — Real Facebook + Instagram posts ============
function MetaLiveTab({ connections }) {
  const [selectedPage, setSelectedPage] = useState(null);
  const [fbPosts, setFbPosts] = useState([]);
  const [igMedia, setIgMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [publishMsg, setPublishMsg] = useState("");
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishStatus, setPublishStatus] = useState("");
  const [activeView, setActiveView] = useState("facebook");

  const page = selectedPage || connections[0];

  const loadData = useCallback(async (p) => {
    if (!p) return;
    setLoading(true); setErr(""); setFbPosts([]); setIgMedia([]);
    try {
      const [fb, ig] = await Promise.allSettled([
        metaApi.fbPosts(p.page_id),
        p.ig_user_id ? metaApi.igMedia(p.page_id) : Promise.resolve({ data: [] }),
      ]);
      if (fb.status === "fulfilled") setFbPosts(fb.value?.data || []);
      if (ig.status === "fulfilled") setIgMedia(ig.value?.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load Meta data");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (connections.length > 0) loadData(selectedPage || connections[0]);
  }, [connections, selectedPage, loadData]);

  const publishToFb = async () => {
    if (!publishMsg.trim() || !page) return;
    setPublishBusy(true); setPublishStatus("");
    try {
      await metaApi.fbPublish(page.page_id, publishMsg);
      setPublishMsg(""); setPublishStatus("Post published to Facebook!");
      await loadData(page);
    } catch (e) {
      setPublishStatus("Failed: " + (e?.response?.data?.detail || "unknown error"));
    }
    setPublishBusy(false);
  };

  if (connections.length === 0) {
    return (
      <div className="flex items-center justify-center h-full mono text-xs text-[#c7c1ff]/50">
        No Meta connections — go to Accounts tab to connect your Facebook Page.
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Page selector + view toggle */}
      <div className="border-b border-gray-100 px-4 py-2 flex items-center gap-3 flex-wrap">
        {connections.length > 1 && (
          <select
            data-testid="meta-page-selector"
            value={page?.page_id || ""}
            onChange={e => { const c = connections.find(x => x.page_id === e.target.value); setSelectedPage(c); loadData(c); }}
            className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
          >
            {connections.map(c => (
              <option key={c.page_id} value={c.page_id}>{c.page_name}</option>
            ))}
          </select>
        )}
        {connections.length === 1 && (
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center rounded-full" style={{ background: "#1877f2" }}>
              <span className="text-white text-[9px] font-bold">f</span>
            </div>
            <span className="text-sm font-medium">{page.page_name}</span>
            {page.ig_username && (
              <span className="text-[11px] text-pink-500 flex items-center gap-1"><Instagram size={11} /> @{page.ig_username}</span>
            )}
          </div>
        )}
        <div className="ml-auto flex items-center gap-1">
          <button
            data-testid="meta-view-fb"
            onClick={() => setActiveView("facebook")}
            className={`px-3 py-1 text-xs mono uppercase tracking-widest ${activeView === "facebook" ? "bg-blue-50 text-blue-600 border border-blue-200" : "text-gray-400 hover:text-gray-600"}`}
            style={{ borderRadius: 4 }}
          >
            <span className="font-bold">f</span> Facebook
          </button>
          {page.ig_user_id && (
            <button
              data-testid="meta-view-ig"
              onClick={() => setActiveView("instagram")}
              className={`px-3 py-1 text-xs mono uppercase tracking-widest ${activeView === "instagram" ? "bg-pink-50 text-pink-600 border border-pink-200" : "text-gray-400 hover:text-gray-600"}`}
              style={{ borderRadius: 4 }}
            >
              <Instagram size={11} /> Instagram
            </button>
          )}
          <button onClick={() => loadData(page)} className="p-1.5 text-gray-400 hover:text-gray-600" title="Refresh">
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      {/* Publish strip */}
      <div className="border-b border-gray-100 px-4 py-2 flex items-center gap-2">
        <input
          data-testid="meta-publish-input"
          value={publishMsg}
          onChange={e => setPublishMsg(e.target.value)}
          placeholder={`Publish to ${page?.page_name || "Facebook Page"}…`}
          className="flex-1 text-sm border border-gray-200 rounded px-3 py-1.5 outline-none focus:border-blue-400"
          onKeyDown={e => e.key === "Enter" && !publishBusy && publishToFb()}
        />
        <button
          data-testid="meta-publish-btn"
          onClick={publishToFb}
          disabled={publishBusy || !publishMsg.trim()}
          className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white transition-opacity"
          style={{ background: "#1877f2", borderRadius: 4, opacity: (publishBusy || !publishMsg.trim()) ? 0.6 : 1 }}
        >
          {publishBusy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
          Post
        </button>
        {publishStatus && <span className={`text-xs mono ${publishStatus.startsWith("Failed") ? "text-red-500" : "text-emerald-600"}`}>{publishStatus}</span>}
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-y-auto p-4">
        {err && <div className="text-xs text-red-500 mono mb-3 p-2 bg-red-50 border border-red-100 rounded">{err}</div>}
        {loading ? (
          <div className="flex items-center gap-2 mono text-xs text-gray-400"><Loader2 size={13} className="animate-spin" /> Loading from Meta…</div>
        ) : activeView === "facebook" ? (
          <div className="space-y-3 max-w-[700px]">
            {fbPosts.length === 0 ? (
              <div className="mono text-xs text-gray-400">No posts found on this Page yet.</div>
            ) : fbPosts.map(p => (
              <div key={p.id} data-testid={`fb-post-${p.id}`} className="border border-gray-100 rounded-lg p-4 bg-white shadow-sm space-y-2">
                <div className="flex items-center justify-between">
                  <span className="mono text-[9px] uppercase tracking-widest text-gray-400">{new Date(p.created_time).toLocaleString()}</span>
                  {p.permalink_url && (
                    <a href={p.permalink_url} target="_blank" rel="noreferrer" className="text-blue-500 hover:text-blue-700">
                      <ExternalLink size={12} />
                    </a>
                  )}
                </div>
                {p.full_picture && <img src={p.full_picture} alt="" className="w-full max-h-64 object-cover rounded" loading="lazy" />}
                {(p.message || p.story) && (
                  <div className="text-sm text-gray-800 whitespace-pre-wrap">{p.message || p.story}</div>
                )}
                <div className="flex items-center gap-4 text-xs text-gray-400 pt-1 border-t border-gray-50">
                  <span className="flex items-center gap-1"><Heart size={11} /> {p.likes?.summary?.total_count ?? 0}</span>
                  <span className="flex items-center gap-1"><MessageCircle size={11} /> {p.comments?.data?.length ?? 0} comments</span>
                </div>
                {p.comments?.data?.length > 0 && (
                  <div className="space-y-1 pt-1">
                    {p.comments.data.slice(0, 3).map(c => (
                      <div key={c.id} className="text-xs text-gray-600 bg-gray-50 rounded px-2 py-1">
                        <span className="font-medium text-gray-700">{c.from?.name || "User"}: </span>{c.message}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-w-[900px]">
            {igMedia.length === 0 ? (
              <div className="col-span-4 mono text-xs text-gray-400">No Instagram media found.</div>
            ) : igMedia.map(m => (
              <a key={m.id} data-testid={`ig-media-${m.id}`} href={m.permalink} target="_blank" rel="noreferrer"
                className="group relative overflow-hidden rounded-lg border border-gray-100 bg-gray-50 aspect-square block">
                {(m.media_url || m.thumbnail_url) ? (
                  <img src={m.media_url || m.thumbnail_url} alt={m.caption || ""} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-300"><ImageIcon size={24} /></div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end p-2 opacity-0 group-hover:opacity-100">
                  <div className="text-white text-xs flex items-center gap-2">
                    <span className="flex items-center gap-1"><Heart size={10} /> {m.like_count ?? 0}</span>
                    <span className="flex items-center gap-1"><MessageCircle size={10} /> {m.comments_count ?? 0}</span>
                  </div>
                </div>
                <div className="absolute top-1 right-1 mono text-[8px] uppercase tracking-widest text-white/80 bg-black/40 px-1 rounded">{m.media_type}</div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Social() {
  const [tab, setTab] = useState("feed");
  const [accounts, setAccounts] = useState([]);
  const [metaConnections, setMetaConnections] = useState([]);

  const loadAccounts = useCallback(async () => {
    try { setAccounts(await socialApi.accounts()); } catch { /* ignore */ }
  }, []);

  const loadMetaConnections = useCallback(async () => {
    try { setMetaConnections(await metaApi.connections()); } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadAccounts(); loadMetaConnections(); }, [loadAccounts, loadMetaConnections]);

  const hasMetaConnection = metaConnections.length > 0;

  return (
    <div className="h-full flex flex-col" style={{ background: "#ffffff" }}>
      <div className="border-b border-[#8b5cf6]/15 px-3 flex items-center overflow-x-auto">
        <TabBtn active={tab === "feed"} onClick={() => setTab("feed")} testid="tab-feed">
          <FileEdit size={12} /> Feed & Compose
        </TabBtn>
        <TabBtn active={tab === "inbox"} onClick={() => setTab("inbox")} testid="tab-inbox">
          <Inbox size={12} /> DM Inbox
        </TabBtn>
        {hasMetaConnection && (
          <TabBtn active={tab === "meta"} onClick={() => setTab("meta")} testid="tab-meta" badge="LIVE">
            <span className="font-bold text-[11px]">f</span> Meta Live
          </TabBtn>
        )}
        <TabBtn active={tab === "ads"} onClick={() => setTab("ads")} testid="tab-ads">
          <Megaphone size={12} /> Ad Campaigns
        </TabBtn>
        <TabBtn active={tab === "accounts"} onClick={() => setTab("accounts")} testid="tab-accounts">
          <LinkIcon size={12} /> Accounts {hasMetaConnection && <CheckCircle2 size={10} className="text-emerald-500" />}
        </TabBtn>
        <div className="ml-auto mono text-[10px] uppercase tracking-widest text-[#c7c1ff]/40 pr-2 flex items-center gap-1 flex-shrink-0">
          <Hash size={10} /> ai output gated by human approval
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        {tab === "feed" && <FeedTab />}
        {tab === "inbox" && <InboxTab />}
        {tab === "meta" && <MetaLiveTab connections={metaConnections} />}
        {tab === "ads" && <AdsTab accounts={accounts} />}
        {tab === "accounts" && (
          <AccountsTab
            accounts={accounts}
            reload={loadAccounts}
            onMetaConnected={() => { loadMetaConnections(); if (!hasMetaConnection) setTab("meta"); }}
          />
        )}
      </div>
    </div>
  );
}

// ============ META CONFIG PANEL (inside Accounts tab) ============
function MetaConfigPanel({ onConnected }) {
  const [cfg, setCfg] = useState(null);
  const [appId, setAppId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connections, setConnections] = useState([]);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState("");
  const [fbReady, setFbReady] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  const loadCfg = useCallback(async () => {
    try {
      const c = await metaApi.getConfig();
      setCfg(c);
      setAppId(c.app_id || "");
    } catch { /* ignore */ }
  }, []);

  const loadConnections = useCallback(async () => {
    try { setConnections(await metaApi.connections()); } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadCfg(); loadConnections(); }, [loadCfg, loadConnections]);

  // Load FB SDK once we have an app_id configured
  useEffect(() => {
    if (!cfg?.configured || !cfg.app_id) return;
    if (window.FB) { setFbReady(true); return; }
    window.fbAsyncInit = () => {
      window.FB.init({ appId: cfg.app_id, cookie: true, xfbml: false, version: cfg.graph_version || "v19.0" });
      setFbReady(true);
    };
    if (!document.getElementById("fb-sdk")) {
      const s = document.createElement("script");
      s.id = "fb-sdk";
      s.src = "https://connect.facebook.net/en_US/sdk.js";
      s.async = true; s.defer = true; s.crossOrigin = "anonymous";
      document.body.appendChild(s);
    }
  }, [cfg]);

  const saveCreds = async () => {
    if (!appId.trim() || !appSecret.trim()) { setErr("Both App ID and App Secret are required."); return; }
    setSaving(true); setErr(""); setStatus("");
    try {
      await metaApi.saveConfig(appId.trim(), appSecret.trim());
      setStatus("Credentials saved securely. You can now connect your Facebook account.");
      setAppSecret(""); setShowConfig(false);
      await loadCfg();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to save credentials");
    }
    setSaving(false);
  };

  const connectFacebook = () => {
    if (!window.FB) { setErr("Facebook SDK not loaded yet — please wait a moment and try again."); return; }
    setConnecting(true); setErr("");
    const scopes = [
      "public_profile", "pages_show_list", "pages_read_engagement",
      "pages_manage_posts", "instagram_basic", "instagram_content_publish"
    ].join(",");
    window.FB.login(async (response) => {
      if (!response.authResponse) {
        setErr("Facebook login was cancelled or failed.");
        setConnecting(false);
        return;
      }
      try {
        const result = await metaApi.connect(response.authResponse.accessToken);
        await loadConnections();
        onConnected?.();
        setStatus(`Connected ${result.count} page(s) successfully.`);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : JSON.stringify(detail);
        setErr(msg || "Failed to connect — check that your app is in development mode and your user is added as a tester.");
      }
      setConnecting(false);
    }, { scope: scopes, return_scopes: true });
  };

  const disconnectAll = async () => {
    if (!window.confirm("Disconnect all Facebook/Instagram connections?")) return;
    try { await metaApi.disconnect(); await loadConnections(); onConnected?.(); setStatus("Disconnected."); } catch { /* ignore */ }
  };

  const isConnected = connections.length > 0;

  return (
    <div
      className="panel p-5 space-y-4"
      data-testid="meta-config-panel"
      style={{ background: "linear-gradient(135deg, rgba(24,119,242,0.06), rgba(225,48,108,0.06))", borderColor: "rgba(24,119,242,0.25)" }}
    >
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 flex items-center justify-center" style={{ background: "#1877f2", borderRadius: 6 }}>
          <span className="text-white font-bold text-sm">f</span>
        </div>
        <div>
          <div className="font-semibold text-sm" style={{ color: "#1877f2" }}>Meta — Facebook &amp; Instagram</div>
          <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/50">real graph api integration</div>
        </div>
        {isConnected && (
          <span className="ml-auto mono text-[9px] uppercase tracking-widest text-[#34d399] flex items-center gap-1 px-2 py-0.5"
            style={{ background: "rgba(52,211,153,0.12)", border: "1px solid rgba(52,211,153,0.3)" }}>
            <CheckCircle2 size={10} /> connected
          </span>
        )}
      </div>

      {/* Connected Pages List */}
      {isConnected && (
        <div className="space-y-2">
          {connections.map(c => (
            <div key={c.page_id} data-testid={`meta-page-${c.page_id}`}
              className="flex items-center gap-2 px-3 py-2"
              style={{ background: "rgba(24,119,242,0.06)", border: "1px solid rgba(24,119,242,0.18)" }}>
              <div className="w-6 h-6 flex items-center justify-center rounded-full" style={{ background: "#1877f2" }}>
                <span className="text-white text-[10px] font-bold">f</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{c.page_name}</div>
                <div className="mono text-[9px] text-[#c7c1ff]/50">Page ID: {c.page_id}</div>
              </div>
              {c.ig_user_id && (
                <div className="flex items-center gap-1 mono text-[9px] text-[#f472b6]"
                  style={{ background: "rgba(244,114,182,0.1)", border: "1px solid rgba(244,114,182,0.25)", padding: "2px 6px" }}>
                  <Instagram size={9} /> {c.ig_username || "linked"}
                </div>
              )}
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <button data-testid="meta-reconnect-btn" onClick={connectFacebook} disabled={connecting || !fbReady}
              className="btn-accent text-[10px] uppercase tracking-widest mono flex items-center gap-1" style={{ background: "#1877f2", color: "#fff" }}>
              {connecting ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />} Reconnect
            </button>
            <button data-testid="meta-disconnect-btn" onClick={disconnectAll}
              className="btn-ghost text-[10px] uppercase tracking-widest mono flex items-center gap-1 text-[#f87171]">
              <X size={10} /> Disconnect All
            </button>
          </div>
        </div>
      )}

      {/* Connect button (when configured but not connected) */}
      {cfg?.configured && !isConnected && (
        <button
          data-testid="meta-connect-btn"
          onClick={connectFacebook}
          disabled={connecting || !fbReady}
          className="w-full flex items-center justify-center gap-2 py-2.5 font-medium text-sm text-white transition-opacity"
          style={{ background: "#1877f2", opacity: (connecting || !fbReady) ? 0.7 : 1 }}
        >
          {connecting ? <Loader2 size={14} className="animate-spin" /> : <span className="font-bold">f</span>}
          {connecting ? "Connecting…" : fbReady ? "Connect with Facebook" : "Loading SDK…"}
        </button>
      )}

      {/* Config section (always available, collapsible when configured) */}
      <div>
        <button
          data-testid="meta-config-toggle"
          onClick={() => setShowConfig(v => !v)}
          className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-[#c7c1ff]/60 hover:text-[#8b5cf6] w-full"
        >
          <Settings size={11} />
          {cfg?.configured ? "Update App Credentials" : "Enter App Credentials"}
          {showConfig ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        </button>

        {(!cfg?.configured || showConfig) && (
          <div className="mt-3 space-y-3" data-testid="meta-creds-form">
            <div>
              <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">Meta App ID</div>
              <input
                data-testid="meta-app-id-input"
                value={appId}
                onChange={e => setAppId(e.target.value)}
                placeholder="Enter your Meta App ID"
                className="w-full bg-white border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400 rounded"
              />
            </div>
            <div>
              <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">Meta App Secret</div>
              <div className="relative">
                <input
                  data-testid="meta-app-secret-input"
                  type={showSecret ? "text" : "password"}
                  value={appSecret}
                  onChange={e => setAppSecret(e.target.value)}
                  placeholder={cfg?.configured ? "Enter new secret to update" : "Enter your Meta App Secret"}
                  className="w-full bg-white border border-gray-200 px-3 py-2 pr-9 text-sm outline-none focus:border-blue-400 rounded"
                />
                <button onClick={() => setShowSecret(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <button
              data-testid="meta-save-config-btn"
              onClick={saveCreds}
              disabled={saving}
              className="btn-accent flex items-center gap-2 text-[11px] uppercase tracking-widest mono"
              style={{ background: "#1877f2", color: "#fff" }}
            >
              {saving ? <Loader2 size={11} className="animate-spin" /> : null}
              Save Credentials
            </button>
            <div className="mono text-[9px] text-[#c7c1ff]/40 leading-relaxed">
              // App ID + Secret are encrypted at rest · Secret is never exposed to the browser after saving
              · Get these from <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer" className="text-[#3b82f6] underline">developers.facebook.com/apps</a>
            </div>
          </div>
        )}
      </div>

      {/* Status / error messages */}
      {err && <div data-testid="meta-error" className="text-[11px] text-red-500 mono">{err}</div>}
      {status && <div data-testid="meta-status" className="text-[11px] text-emerald-600 mono">{status}</div>}
    </div>
  );
}

function AccountsTab({ accounts, reload, onMetaConnected }) {
  const [editing, setEditing] = useState(null); // platform id
  const [handle, setHandle] = useState("");
  const [busy, setBusy] = useState(false);

  const connect = async (platform) => {
    setBusy(true);
    try {
      await socialAuthApi.connect(platform, handle || `@${platform}user`);
      setEditing(null); setHandle("");
      reload?.();
    } catch { /* ignore */ }
    setBusy(false);
  };
  const disconnect = async (platform) => {
    setBusy(true);
    try { await socialAuthApi.disconnect(platform); reload?.(); } catch { /* ignore */ }
    setBusy(false);
  };

  return (
    <div className="p-5 overflow-y-auto h-full">
      <div className="flex flex-col lg:flex-row gap-6 max-w-[1200px]">

        {/* Left column: Platform mock connections */}
        <div className="flex-1 space-y-4">
          <div className="mono text-[10px] uppercase tracking-widest text-[#c7c1ff]/60">// platform connections (hexa internal)</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {PLATFORMS.map(p => {
              const acc = accounts.find(a => a.platform === p.id);
              const connected = acc?.connected;
              const { Icon } = p;
              return (
                <div
                  key={p.id}
                  data-testid={`account-card-${p.id}`}
                  className="panel p-4"
                  style={{
                    background: connected
                      ? `linear-gradient(160deg, ${p.color}1A, ${p.color}05)`
                      : "rgba(255,255,255,0.02)",
                    borderColor: connected ? `${p.color}55` : "rgba(168,227,239,0.18)",
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Icon size={18} color={p.color} />
                    <div className="font-medium text-sm" style={{ color: p.color }}>{p.label}</div>
                    {connected ? (
                      <span className="ml-auto mono text-[9px] uppercase tracking-widest text-[#34d399] flex items-center gap-1">
                        <CheckCircle2 size={10} /> on
                      </span>
                    ) : (
                      <span className="ml-auto mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/40">off</span>
                    )}
                  </div>
                  {connected && (
                    <div className="mt-2 mono text-[10px] text-[#c7c1ff]/65">
                      {acc.handle}
                    </div>
                  )}
                  {editing === p.id ? (
                    <div className="mt-3 space-y-2">
                      <input
                        data-testid={`connect-handle-${p.id}`}
                        value={handle}
                        onChange={(e) => setHandle(e.target.value)}
                        placeholder={p.id === "linkedin" ? "linkedin-handle" : `@${p.id}_handle`}
                        className="w-full bg-white border border-gray-200 px-2 py-1.5 text-xs outline-none focus:border-purple-400 rounded"
                      />
                      <div className="flex gap-2">
                        <button
                          data-testid={`connect-confirm-${p.id}`}
                          onClick={() => connect(p.id)}
                          disabled={busy}
                          className="btn-accent text-[10px] uppercase tracking-widest mono flex items-center gap-1"
                        >
                          {busy ? <Loader2 size={10} className="animate-spin" /> : <LinkIcon size={10} />} Save
                        </button>
                        <button onClick={() => { setEditing(null); setHandle(""); }} className="btn-ghost text-[10px] uppercase tracking-widest mono">×</button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3">
                      {connected ? (
                        <button
                          data-testid={`disconnect-${p.id}`}
                          onClick={() => disconnect(p.id)}
                          className="btn-ghost text-[10px] uppercase tracking-widest mono flex items-center gap-1 text-[#f87171]"
                        >
                          <X size={10} /> remove
                        </button>
                      ) : (
                        <button
                          data-testid={`connect-start-${p.id}`}
                          onClick={() => { setEditing(p.id); setHandle(acc?.handle || ""); }}
                          className="btn-accent text-[10px] uppercase tracking-widest mono flex items-center gap-1"
                        >
                          <LinkIcon size={10} /> connect
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right column: Meta (Facebook/Instagram) real OAuth */}
        <div className="w-full lg:w-[420px] space-y-3">
          <div className="mono text-[10px] uppercase tracking-widest text-[#c7c1ff]/60">// meta — real oauth integration</div>
          <MetaConfigPanel onConnected={onMetaConnected} />
        </div>

      </div>
    </div>
  );
}

function AdCampaignCard({ c, onChange }) {
  const [busy, setBusy] = useState(false);
  const meta = platMeta(c.platform);
  const { Icon } = meta;

  const toggleStatus = async () => {
    setBusy(true);
    try {
      const next = c.status === "active" ? "paused" : "active";
      const updated = await campaignsApi.update(c.id, { status: next });
      onChange?.(updated);
    } catch { /* ignore */ }
    setBusy(false);
  };
  const remove = async () => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    setBusy(true);
    try { await campaignsApi.remove(c.id); onChange?.({ ...c, _deleted: true }); } catch { /* ignore */ }
    setBusy(false);
  };

  const progress = Math.min(100, Math.round((c.budget_spent / c.budget_total) * 100));

  return (
    <div className="panel p-4 space-y-3" data-testid={`campaign-${c.id}`}>
      <div className="flex items-center gap-2">
        <Icon size={14} color={meta.color} />
        <div className="font-medium" style={{ color: meta.color }}>{c.name}</div>
        <span
          className="ml-2 mono text-[9px] uppercase tracking-widest px-1.5 py-0.5"
          style={{
            background: c.status === "active" ? "rgba(52,211,153,0.18)" : c.status === "paused" ? "rgba(59,130,246,0.18)" : "rgba(248,113,113,0.18)",
            color: c.status === "active" ? "#34d399" : c.status === "paused" ? "#3b82f6" : "#f87171",
            border: `1px solid ${c.status === "active" ? "#34d399" : c.status === "paused" ? "#3b82f6" : "#f87171"}55`,
          }}
        >
          {c.status}
        </span>
        <div className="ml-auto flex gap-1">
          <button data-testid={`campaign-toggle-${c.id}`} onClick={toggleStatus} disabled={busy} className="p-1.5 hover:bg-[#8b5cf6]/10" title={c.status === "active" ? "Pause" : "Resume"}>
            {c.status === "active" ? <Pause size={12} /> : <Play size={12} />}
          </button>
          <button data-testid={`campaign-delete-${c.id}`} onClick={remove} disabled={busy} className="p-1.5 hover:bg-red-500/10 text-red-400" title="Delete">
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      <div className="bg-[#0e1a22] border border-[#8b5cf6]/15 p-3">
        <div className="text-sm font-medium text-[#3b82f6] mb-1">{c.headline}</div>
        <div className="text-xs text-[#e8f4f8]/85 whitespace-pre-wrap mb-2">{c.body}</div>
        <div className="inline-block mono text-[10px] uppercase tracking-widest px-2 py-0.5" style={{ background: meta.color + "22", border: `1px solid ${meta.color}55`, color: meta.color }}>{c.cta}</div>
      </div>

      <div className="grid grid-cols-4 gap-3 mono text-[10px]">
        <div>
          <div className="text-[#c7c1ff]/55 uppercase tracking-widest">Impressions</div>
          <div className="text-base text-[#a78bfa] mt-0.5">{c.stats?.impressions?.toLocaleString() || 0}</div>
        </div>
        <div>
          <div className="text-[#c7c1ff]/55 uppercase tracking-widest">Clicks</div>
          <div className="text-base text-[#8b5cf6] mt-0.5">{c.stats?.clicks?.toLocaleString() || 0}</div>
        </div>
        <div>
          <div className="text-[#c7c1ff]/55 uppercase tracking-widest">CTR</div>
          <div className="text-base text-[#3b82f6] mt-0.5">{c.stats?.ctr || 0}%</div>
        </div>
        <div>
          <div className="text-[#c7c1ff]/55 uppercase tracking-widest">Conversions</div>
          <div className="text-base text-[#34d399] mt-0.5">{c.stats?.conversions?.toLocaleString() || 0}</div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">
          <span>Budget</span>
          <span>${c.budget_spent?.toFixed(2)} / ${c.budget_total?.toFixed(2)}</span>
        </div>
        <div className="h-2 bg-[#0e1a22] border border-[#8b5cf6]/15 relative">
          <div className="h-full" style={{ width: `${progress}%`, background: "linear-gradient(90deg,#8b5cf6,#3b82f6)" }} />
        </div>
        <div className="mono text-[9px] text-[#c7c1ff]/45 mt-1">
          // ${c.budget_daily?.toFixed(2)}/day · audience: {c.audience || "untargeted"}
        </div>
      </div>
    </div>
  );
}

function AdsTab({ accounts }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setCampaigns(await campaignsApi.list()); } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  // refresh stats every 15s
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-3 px-5 py-3 border-b border-[#8b5cf6]/10">
        <Megaphone size={14} color="#3b82f6" />
        <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#3b82f6]">// ad campaigns</div>
        <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/45 ml-2">
          {campaigns.filter(c => c.status === "active").length} active / {campaigns.length} total
        </div>
        <button onClick={load} className="ml-auto text-[#c7c1ff]/60 hover:text-[#8b5cf6] p-1" title="Refresh"><RefreshCw size={13} /></button>
        <button
          data-testid="campaign-new"
          onClick={() => setOpenCreate(true)}
          className="btn-accent text-[11px] uppercase tracking-widest mono flex items-center gap-1.5"
        >
          <Plus size={12} /> New Campaign
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5">
        {loading && campaigns.length === 0 ? (
          <div className="mono text-[10px] text-[#c7c1ff]/50 uppercase tracking-widest">loading…</div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-16">
            <Megaphone size={40} color="#8b5cf6" className="mx-auto opacity-30 mb-3" />
            <div className="mono text-xs text-[#c7c1ff]/60 uppercase tracking-widest">// no campaigns yet</div>
            <button
              data-testid="campaign-new-empty"
              onClick={() => setOpenCreate(true)}
              className="mt-4 btn-accent text-[11px] uppercase tracking-widest mono flex items-center gap-1.5 mx-auto"
            >
              <Plus size={12} /> Create First Campaign
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {campaigns.filter(c => !c._deleted).map(c => (
              <AdCampaignCard key={c.id} c={c} onChange={(u) => {
                if (u._deleted) setCampaigns(campaigns.filter(x => x.id !== c.id));
                else setCampaigns(campaigns.map(x => x.id === u.id ? u : x));
              }} />
            ))}
          </div>
        )}
      </div>

      {openCreate && (
        <CreateCampaignModal
          accounts={accounts}
          onClose={() => setOpenCreate(false)}
          onCreated={() => { setOpenCreate(false); load(); }}
        />
      )}
    </div>
  );
}

function CreateCampaignModal({ accounts, onClose, onCreated }) {
  const [platform, setPlatform] = useState("twitter");
  const [name, setName] = useState("");
  const [product, setProduct] = useState("");
  const [target, setTarget] = useState("");
  const [objective, setObjective] = useState("engagement");
  const [budget_total, setBudgetTotal] = useState(100);
  const [budget_daily, setBudgetDaily] = useState(10);
  const [headline, setHeadline] = useState("");
  const [body, setBody] = useState("");
  const [cta, setCta] = useState("Learn More");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [err, setErr] = useState("");

  const aiFill = async () => {
    if (!product.trim() || !target.trim()) { setErr("Product and target audience are required for AI draft"); return; }
    setAiBusy(true); setErr("");
    try {
      const d = await campaignsApi.aiDraft(platform, product, target, objective);
      setHeadline(d.headline || "");
      setBody(d.body || "");
      setCta(d.cta || "Learn More");
      if (!target && d.audience_suggestion) setTarget(d.audience_suggestion);
    } catch (e) {
      setErr("AI draft failed");
    }
    setAiBusy(false);
  };

  const create = async () => {
    if (!name.trim() || !headline.trim() || !body.trim()) {
      setErr("Name, headline and body are required");
      return;
    }
    setBusy(true); setErr("");
    try {
      await campaignsApi.create({
        platform, name, objective, audience: target,
        budget_total: Number(budget_total), budget_daily: Number(budget_daily),
        headline, body, cta,
      });
      onCreated?.();
    } catch (e) {
      setErr("Failed to create");
    }
    setBusy(false);
  };

  const accConnected = accounts.find(a => a.platform === platform)?.connected;

  return (
    <div className="absolute inset-0 z-[50] flex items-center justify-center" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)" }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="panel-strong w-[640px] max-w-[94%] max-h-[88vh] overflow-y-auto p-5"
        style={{ clipPath: "polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 16px 100%, 0 calc(100% - 16px))" }}
        data-testid="create-campaign-modal"
      >
        <div className="flex items-center gap-2 mb-4">
          <Megaphone size={14} color="#3b82f6" />
          <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#3b82f6]">// new ad campaign</div>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          {PLATFORMS.map(p => {
            const { Icon } = p;
            const active = platform === p.id;
            return (
              <button
                key={p.id}
                data-testid={`campaign-platform-${p.id}`}
                onClick={() => setPlatform(p.id)}
                className="p-3 mono text-[10px] uppercase tracking-widest flex items-center justify-center gap-1.5"
                style={{
                  background: active ? `${p.color}22` : "transparent",
                  border: `1px solid ${active ? p.color + "88" : "rgba(139,92,246,0.18)"}`,
                  color: active ? p.color : "#c7c1ff",
                }}
              >
                <Icon size={12} /> {p.label}
              </button>
            );
          })}
        </div>

        {!accConnected && (
          <div className="mb-3 p-2 mono text-[10px] text-[#3b82f6] bg-[#3b82f6]/8 border border-[#3b82f6]/30">
            // note: this platform isn't connected — campaign will be saved but won't run live until you connect it in the Accounts tab.
          </div>
        )}

        <div className="space-y-3">
          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">campaign name</div>
            <input
              data-testid="campaign-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Q2 Launch Week"
              className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60 mono"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">objective</div>
              <select
                value={objective}
                onChange={(e) => setObjective(e.target.value)}
                className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60 mono"
                data-testid="campaign-objective"
              >
                <option value="awareness">Awareness</option>
                <option value="engagement">Engagement</option>
                <option value="traffic">Traffic</option>
                <option value="leads">Leads</option>
                <option value="conversions">Conversions</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">total $</div>
                <input data-testid="campaign-budget-total" type="number" value={budget_total} onChange={(e) => setBudgetTotal(e.target.value)} className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60 mono" />
              </div>
              <div>
                <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">daily $</div>
                <input data-testid="campaign-budget-daily" type="number" value={budget_daily} onChange={(e) => setBudgetDaily(e.target.value)} className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60 mono" />
              </div>
            </div>
          </div>

          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">product / service to advertise</div>
            <input data-testid="campaign-product" value={product} onChange={(e) => setProduct(e.target.value)} placeholder="e.g. our new AI productivity tool" className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60 mono" />
          </div>

          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">target audience</div>
            <input data-testid="campaign-target" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="e.g. SaaS founders, age 25-45" className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60 mono" />
          </div>

          <button data-testid="campaign-ai-fill" onClick={aiFill} disabled={aiBusy} className="btn-ghost text-[11px] uppercase tracking-widest mono flex items-center gap-1.5 w-full justify-center">
            {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI Draft Headline · Body · CTA
          </button>

          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">headline</div>
            <input data-testid="campaign-headline" value={headline} onChange={(e) => setHeadline(e.target.value)} className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60" />
          </div>
          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">body / caption</div>
            <textarea data-testid="campaign-body" value={body} onChange={(e) => setBody(e.target.value)} rows={3} className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60" />
          </div>
          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">cta button text</div>
            <input data-testid="campaign-cta" value={cta} onChange={(e) => setCta(e.target.value)} className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60 mono" />
          </div>

          {err && <div className="text-xs text-[#f87171] mono">{err}</div>}

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="btn-ghost text-[11px] uppercase tracking-widest mono">cancel</button>
            <button data-testid="campaign-create-submit" onClick={create} disabled={busy} className="btn-accent text-[11px] uppercase tracking-widest mono flex items-center gap-1.5">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Megaphone size={12} />} Launch Campaign
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
